'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SearchFiltersProps {
  onSearch: (query: string) => void;
  onFilterChange: (filters: FilterState) => void;
}

interface FilterState {
  category: string;
  year: string;
  methodology: string;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({ onSearch, onFilterChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>({
    category: 'all',
    year: 'all',
    methodology: 'all',
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch(value);
  };

  const handleFilterChange = (filterType: keyof FilterState, value: string) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'forgiveness', label: 'Forgiveness Psychology' },
    { value: 'peace', label: 'Peace Building' },
    { value: 'conflict', label: 'Conflict Resolution' },
    { value: 'trauma', label: 'Trauma Healing' },
    { value: 'community', label: 'Community Impact' },
  ];

  const years = [
    { value: 'all', label: 'All Years' },
    { value: '2026', label: '2026' },
    { value: '2025', label: '2025' },
    { value: '2024', label: '2024' },
    { value: '2023', label: '2023' },
    { value: 'older', label: 'Before 2023' },
  ];

  const methodologies = [
    { value: 'all', label: 'All Methodologies' },
    { value: 'quantitative', label: 'Quantitative' },
    { value: 'qualitative', label: 'Qualitative' },
    { value: 'mixed', label: 'Mixed Methods' },
    { value: 'meta', label: 'Meta-Analysis' },
    { value: 'case', label: 'Case Study' },
  ];

  return (
    <div className="bg-card rounded-lg shadow-md p-6 mb-8 border border-border">
      <div className="space-y-6">
        {/* Search Bar */}
        <div className="relative">
          <Icon
            name="MagnifyingGlassIcon"
            size={20}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search research papers, authors, keywords..."
            className="w-full pl-12 pr-4 py-3 border border-input rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-body font-medium text-foreground mb-2">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-body font-medium text-foreground mb-2">
              Publication Year
            </label>
            <select
              value={filters.year}
              onChange={(e) => handleFilterChange('year', e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {years.map((year) => (
                <option key={year.value} value={year.value}>
                  {year.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-body font-medium text-foreground mb-2">
              Methodology
            </label>
            <select
              value={filters.methodology}
              onChange={(e) => handleFilterChange('methodology', e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {methodologies.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchFilters;